import { useState } from 'react';
import type {
  ProjectWithDetails,
  ReferenceImage,
  StoryElement,
} from '@historyjki/shared';
import { api, toastError, uploadReferenceImage } from '../utils/api';
import { generateImageForRef } from '../components/image-editor/api';
import { useToast } from './useToast';

interface UseCastEditorArgs {
  element: StoryElement;
  project: ProjectWithDetails;
  /** Refetch project data after reference-image mutations. */
  refetch: () => Promise<unknown>;
}

/**
 * Reference-image business logic for the cast editor modal: generate / regenerate
 * portrait (generateImageForRef) and upload (uploadReferenceImage), with
 * their busy-state tracking. The element save/delete still flow through the
 * project mutations passed into the modal as props.
 */
export function useCastEditor({ element, project, refetch }: UseCastEditorArgs) {
  const { showToast } = useToast();
  const [generatingPortrait, setGeneratingPortrait] = useState(false);
  const [regeneratingRefIds, setRegeneratingRefIds] = useState<Set<string>>(() => new Set());
  const [deletingRefIds, setDeletingRefIds] = useState<Set<string>>(() => new Set());

  const handleGeneratePortrait = async () => {
    setGeneratingPortrait(true);
    try {
      await generateImageForRef({
        kind: 'reference_image',
        project_id: project.id,
        owner_type: 'story_element',
        owner_id: element.id,
        reference_image_id: null,
      });
      await refetch();
      showToast('Reference image generated', 'success');
    } catch (err) {
      toastError(showToast, err, 'Failed to generate reference image');
    } finally {
      setGeneratingPortrait(false);
    }
  };

  const handleRegenerate = async (img: ReferenceImage) => {
    setRegeneratingRefIds((prev) => new Set(prev).add(img.id));
    try {
      await generateImageForRef({
        kind: 'reference_image',
        project_id: project.id,
        owner_type: 'story_element',
        owner_id: img.owner_id,
        reference_image_id: img.id,
      });
      await refetch();
      showToast('Reference image regenerated', 'success');
    } catch (err) {
      toastError(showToast, err, 'Failed to regenerate reference image');
    } finally {
      setRegeneratingRefIds((prev) => {
        const next = new Set(prev);
        next.delete(img.id);
        return next;
      });
    }
  };

  const handleRefUpload = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    try {
      await uploadReferenceImage('story_element', element.id, file);
      await refetch();
      showToast('Reference image uploaded', 'success');
    } catch (err) {
      toastError(showToast, err, 'Failed to upload image');
    }
  };

  const handleRefDelete = async (img: ReferenceImage) => {
    setDeletingRefIds((prev) => new Set(prev).add(img.id));
    try {
      await api.delete(`/upload/${img.id}`);
      await refetch();
      showToast('Reference image removed', 'success');
    } catch (err) {
      toastError(showToast, err, 'Failed to delete reference image');
    } finally {
      setDeletingRefIds((prev) => {
        const next = new Set(prev);
        next.delete(img.id);
        return next;
      });
    }
  };

  return {
    generatingPortrait,
    regeneratingRefIds,
    deletingRefIds,
    handleGeneratePortrait,
    handleRegenerate,
    handleRefUpload,
    handleRefDelete,
  };
}
